/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – AnalysisPanel.tsx FINAL
// Adăugat: Select All/Deselect All + Istoric zilnic real
// ============================================================

import React, { useState, useMemo } from 'react';
import { Select, Button, Tooltip } from 'antd';
import {
  BarChartOutlined, RiseOutlined, FallOutlined,
  ExperimentOutlined, SwapOutlined, CheckSquareOutlined,
  BorderOutlined, LineChartOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, ReferenceLine,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  AreaChart, Area,
} from 'recharts';

import { useSimulatorData } from '../../context/SimulatorContext';
import { sensorHistory, mockSensors } from '../../data/mockData';
import { SensorData } from '../../types';

const { Option } = Select;

const PALETTE = [
  '#6366f1','#10b981','#f59e0b','#ef4444','#06b6d4',
  '#8b5cf6','#f97316','#84cc16','#ec4899','#14b8a6',
  '#3b82f6','#a855f7','#22c55e','#eab308','#64748b',
];

// ─── Helpers statistici ───────────────────────────────────

const calcStats = (vals: number[]) => {
  if (!vals.length) return { min: 0, max: 0, avg: 0, std: 0, trend: 0 };
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
  const std = Math.sqrt(vals.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / vals.length);
  const first5 = vals.slice(0, 5).reduce((s, v) => s + v, 0) / 5;
  const last5  = vals.slice(-5).reduce((s, v) => s + v, 0) / 5;
  const trend  = first5 > 0 ? ((last5 - first5) / first5) * 100 : 0;
  return { min, max, avg, std, trend };
};

// ─── Section wrapper ──────────────────────────────────────

const Panel: React.FC<{
  icon: React.ReactNode; title: string; sub?: string; color?: string;
  children: React.ReactNode; extra?: React.ReactNode;
}> = ({ icon, title, sub, color = '#6366f1', children, extra }) => (
  <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
    <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>}
      </div>
      {extra}
    </div>
    <div style={{ padding: '16px 18px' }}>{children}</div>
  </div>
);

// ─── Custom tooltip ───────────────────────────────────────

const MultiTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', fontSize: 11, minWidth: 150 }}>
      <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color, display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 2 }}>
          <span>{p.name}</span>
          <strong>{typeof p.value === 'number' ? p.value.toFixed(4) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ─── Panel principal ──────────────────────────────────────

const AnalysisPanel: React.FC = () => {
  const { sensors, liveHistory } = useSimulatorData();

  const ALL_IDS = mockSensors.map(s => s.id);
  const [selected,  setSelected]  = useState<string[]>([ALL_IDS[0], ALL_IDS[1], ALL_IDS[2]]);
  const [scatterX,  setScatterX]  = useState(ALL_IDS[0]);
  const [scatterY,  setScatterY]  = useState(ALL_IDS[1]);
  const [histMode,  setHistMode]  = useState<'hourly' | 'daily'>('hourly');
  const [chartType, setChartType] = useState<'line' | 'area'>('line');

  const selectAll   = () => setSelected([...ALL_IDS]);
  const deselectAll = () => setSelected([ALL_IDS[0]]);

  // Date pentru graficul multi-senzor
  const multiData = useMemo(() => {
    const histories = selected.map(id => {
      const lh = liveHistory.find(h => h.sensorId === id);
      const sh = sensorHistory.find(h => h.sensorId === id);
      const raw = histMode === 'hourly'
        ? (lh?.data ?? sh?.data ?? []).slice(-48)
        : (sh?.data ?? []).filter((_, i) => i % 24 === 0).slice(-30);
      return { id, data: raw };
    });

    const maxLen = Math.max(...histories.map(h => h.data.length));
    return Array.from({ length: maxLen }, (_, i) => {
      const point: Record<string, any> = {
        time: histories[0]?.data[i]
          ? new Date(histories[0].data[i].timestamp).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: histMode === 'hourly' ? '2-digit' : undefined, minute: histMode === 'hourly' ? '2-digit' : undefined })
          : '',
      };
      histories.forEach(h => {
        const sensor = mockSensors.find(s => s.id === h.id);
        if (h.data[i]) point[sensor?.name ?? h.id] = h.data[i].value;
      });
      return point;
    });
  }, [selected, liveHistory, histMode]);

  // Statistici per senzor selectat
  const statsData = useMemo(() =>
    selected.map(id => {
      const sensor = sensors.find(s => s.id === id) ?? mockSensors.find(s => s.id === id)!;
      const lh     = liveHistory.find(h => h.sensorId === id);
      const sh     = sensorHistory.find(h => h.sensorId === id);
      const vals   = (lh?.data ?? sh?.data ?? []).slice(-48).map(p => p.value);
      const stats  = calcStats(vals);
      return { ...stats, id, name: sensor.name, parameter: sensor.parameter, unit: sensor.unit, lod: sensor.lod, color: PALETTE[ALL_IDS.indexOf(id) % PALETTE.length] };
    }),
    [selected, sensors, liveHistory]
  );

  // Date radar
  const radarData = useMemo(() =>
    statsData.map(s => ({
      param:   s.parameter.length > 12 ? s.parameter.slice(0, 12) + '…' : s.parameter,
      valPct:  Math.min(100, Math.round((s.avg / (s.lod * 2)) * 100)),
      maxPct:  Math.min(100, Math.round((s.max / (s.lod * 2)) * 100)),
    })),
    [statsData]
  );

  // Date scatter corelație
  const sX = sensors.find(s => s.id === scatterX);
  const sY = sensors.find(s => s.id === scatterY);
  const shX = (liveHistory.find(h => h.sensorId === scatterX) ?? sensorHistory.find(h => h.sensorId === scatterX))?.data ?? [];
  const shY = (liveHistory.find(h => h.sensorId === scatterY) ?? sensorHistory.find(h => h.sensorId === scatterY))?.data ?? [];
  const minLen = Math.min(shX.length, shY.length, 60);
  const scatterData = Array.from({ length: minLen }, (_, i) => ({
    x: shX[shX.length - minLen + i]?.value ?? 0,
    y: shY[shY.length - minLen + i]?.value ?? 0,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>Analiză</h2>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>Comparație multi-senzor · Corelații · Statistici avansate</span>
      </div>

      {/* Selector senzori */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>Senzori analizați:</span>
          <Select
            mode="multiple" value={selected} onChange={setSelected}
            style={{ flex: 1, minWidth: 300 }} maxTagCount={5}
            placeholder="Selectează senzori…"
            optionFilterProp="children"
          >
            {mockSensors.map(s => (
              <Option key={s.id} value={s.id}>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
                <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 6 }}>{s.parameter} · {s.location.label}</span>
              </Option>
            ))}
          </Select>
          <div style={{ display: 'flex', gap: 6 }}>
            <Tooltip title="Selectează toți senzori">
              <Button icon={<CheckSquareOutlined />} size="small" onClick={selectAll}>Toți ({ALL_IDS.length})</Button>
            </Tooltip>
            <Tooltip title="Deselectează toți">
              <Button icon={<BorderOutlined />} size="small" onClick={deselectAll}>Golește</Button>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            {(['hourly', 'daily'] as const).map(m => (
              <button key={m} onClick={() => setHistMode(m)} style={{ padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: histMode === m ? 700 : 500, border: `1px solid ${histMode === m ? '#6366f1' : '#e2e8f0'}`, background: histMode === m ? '#eef2ff' : '#fff', color: histMode === m ? '#6366f1' : '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
                {m === 'hourly' ? 'Orar' : 'Zilnic'}
              </button>
            ))}
            {(['line', 'area'] as const).map(t => (
              <button key={t} onClick={() => setChartType(t)} style={{ padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: chartType === t ? 700 : 500, border: `1px solid ${chartType === t ? '#6366f1' : '#e2e8f0'}`, background: chartType === t ? '#eef2ff' : '#fff', color: chartType === t ? '#6366f1' : '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
                {t === 'line' ? 'Linie' : 'Zonă'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grafic principal multi-senzor */}
      <Panel icon={<LineChartOutlined />} title="Evoluție comparativă" color="#6366f1"
        sub={`${selected.length} senzori · ${histMode === 'hourly' ? 'Ultimele 48h (orar)' : 'Ultimele 30 zile (zilnic)'}`}
      >
        <ResponsiveContainer width="100%" height={280}>
          {chartType === 'area' ? (
            <AreaChart data={multiData} margin={{ top: 8, right: 24, bottom: 0, left: -8 }}>
              <defs>
                {selected.map((id, i) => (
                  <linearGradient key={id} id={`ag-${id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={PALETTE[i % PALETTE.length]} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={PALETTE[i % PALETTE.length]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(multiData.length / 12))} />
              <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <RTooltip content={<MultiTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {selected.map((id, i) => {
                const s = mockSensors.find(x => x.id === id)!;
                return <Area key={id} type="monotone" dataKey={s.name} stroke={PALETTE[i % PALETTE.length]} fill={`url(#ag-${id})`} strokeWidth={2} dot={false} />;
              })}
            </AreaChart>
          ) : (
            <LineChart data={multiData} margin={{ top: 8, right: 24, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(multiData.length / 12))} />
              <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <RTooltip content={<MultiTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {selected.map((id, i) => {
                const s = mockSensors.find(x => x.id === id)!;
                return <Line key={id} type="monotone" dataKey={s.name} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />;
              })}
            </LineChart>
          )}
        </ResponsiveContainer>
      </Panel>

      {/* Statistici tabelare */}
      <Panel icon={<BarChartOutlined />} title="Statistici per senzor" color="#10b981" sub="Medie, min, max, deviație standard, trend">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Senzor', 'Parametru', 'Medie', 'Min', 'Max', 'Std Dev', 'Trend', 'vs LOD'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {statsData.map((s, i) => (
                <tr key={s.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{s.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#475569' }}>{s.parameter}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: s.avg >= s.lod ? '#ef4444' : '#10b981' }}>
                    {s.avg.toFixed(4)} <span style={{ fontSize: 10, color: '#94a3b8' }}>{s.unit}</span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{s.min.toFixed(4)}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{s.max.toFixed(4)}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>±{s.std.toFixed(4)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, color: s.trend > 5 ? '#ef4444' : s.trend < -5 ? '#10b981' : '#94a3b8' }}>
                      {s.trend > 5 ? <RiseOutlined /> : s.trend < -5 ? <FallOutlined /> : null}
                      {s.trend > 0 ? '+' : ''}{s.trend.toFixed(1)}%
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ height: 6, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden', width: 80 }}>
                      <div style={{ height: '100%', borderRadius: 99, background: s.avg >= s.lod ? '#ef4444' : '#10b981', width: `${Math.min(100, (s.avg / (s.lod * 2)) * 100)}%` }} />
                    </div>
                    <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>{Math.round((s.avg / s.lod) * 100)}% din LOD</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Radar + Scatter */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Radar */}
        <Panel icon={<ExperimentOutlined />} title="Profil radar contaminanți" color="#8b5cf6" sub="Concentrație medie % din 2×LOD">
          <ResponsiveContainer width="100%" height={240}>
            <RadarChart data={radarData} margin={{ top: 8, right: 32, bottom: 8, left: 32 }}>
              <PolarGrid stroke="#f1f5f9" />
              <PolarAngleAxis dataKey="param" tick={{ fontSize: 9, fill: '#64748b' }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8, fill: '#94a3b8' }} />
              <Radar name="Medie" dataKey="valPct" stroke="#6366f1" fill="#6366f1" fillOpacity={0.18} />
              <Radar name="Max"   dataKey="maxPct" stroke="#ef4444" fill="#ef4444" fillOpacity={0.10} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11 }} formatter={(v: any) => [`${v}%`]} />
            </RadarChart>
          </ResponsiveContainer>
        </Panel>

        {/* Scatter corelație */}
        <Panel icon={<SwapOutlined />} title="Corelație parametri" color="#f59e0b" sub="Relația de concentrație între 2 substanțe"
          extra={
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select value={scatterX} onChange={setScatterX} size="small" style={{ width: 130 }}>
                {sensors.map(s => <Option key={s.id} value={s.id}>{s.name.split(' ')[0] + ' ' + s.name.split(' ')[1]}</Option>)}
              </Select>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>vs</span>
              <Select value={scatterY} onChange={setScatterY} size="small" style={{ width: 130 }}>
                {sensors.map(s => <Option key={s.id} value={s.id}>{s.name.split(' ')[0] + ' ' + s.name.split(' ')[1]}</Option>)}
              </Select>
            </div>
          }
        >
          <ResponsiveContainer width="100%" height={200}>
            <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" dataKey="x" name={sX?.parameter}
                tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                label={{ value: sX?.unit ?? '', position: 'insideRight', fontSize: 9, fill: '#94a3b8' }}
              />
              <YAxis type="number" dataKey="y" name={sY?.parameter}
                tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                label={{ value: sY?.unit ?? '', angle: -90, position: 'insideTop', fontSize: 9, fill: '#94a3b8' }}
              />
              <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11 }} cursor={{ strokeDasharray: '3 3' }}
                formatter={(v: any, n: any) => [v.toFixed(4), n === 'x' ? sX?.parameter : sY?.parameter]}
              />
              <Scatter data={scatterData} fill="#6366f1" fillOpacity={0.6} r={3} />
            </ScatterChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Bar chart comparativ medie vs LOD */}
      <Panel icon={<BarChartOutlined />} title="Comparativ medii față de LOD" color="#ef4444" sub="Barele roșii = depășire LOD medie">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart
            data={statsData.map(s => ({ name: s.name.split(' ')[1] ?? s.name, avg: parseFloat(s.avg.toFixed(4)), lod: s.lod, color: s.avg >= s.lod ? '#ef4444' : '#10b981' }))}
            margin={{ top: 4, right: 16, bottom: 0, left: -8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11 }} />
            <Bar dataKey="avg"  name="Medie" radius={[4,4,0,0]} fill="#6366f1" />
            <Bar dataKey="lod"  name="LOD"   radius={[4,4,0,0]} fill="#f1f5f9" />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
};

export default AnalysisPanel;